import * as React from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  TriangleAlert,
  Upload,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/components/ui/use-toast"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

import { cargar, descargarEjemplo, previsualizar } from "@/routes/carga/api"
import type { Previsualizacion } from "@/routes/carga/mapping"

/**
 * CARGAR UN ARCHIVO SIN SALIR DEL PASO.
 *
 * Es la pieza que contestan juntos los puntos 16, 17, 19 y 20 de Cesar, que son
 * el mismo encargo dicho cuatro veces:
 *
 *   17 · «cuando le das click a cargar bancos y cotizaciones te lleva adentro de
 *         la plataforma, deberías de poder cargar todo en el onboarding»
 *   19 · «cargar facturación igual deberías de poder cargarlo ahí mismo: manual
 *         por cliente, o en bulk, o con alguna plantilla»
 *   20 · «los últimos 3 puntos igual debería de botártelo todo en el onboarding»
 *
 * ===========================================================================
 * POR QUÉ UN ENLACE A OTRA PANTALLA ERA EL PROBLEMA, Y NO UN ATAJO
 * ===========================================================================
 *
 * El traspaso es un CUESTIONARIO: una pregunta a la vez, y cada respuesta deja
 * el paso en verde. Un enlace que lleva a `/finanzas/datos` rompe eso en el
 * segundo paso — quien llega de otra plataforma no conoce el menú, hace el
 * trabajo allí y **no vuelve**, porque nada le dice que quedaban nueve
 * preguntas. El traspaso se queda a medias sin que nadie lo abandone a
 * propósito.
 *
 * ---------------------------------------------------------------------------
 * LAS TRES VÍAS QUE PIDIÓ CESAR, Y LA PREVISUALIZACIÓN NO ES OPCIONAL
 *
 * Esta pieza da la vía de ARCHIVO (bulk y plantilla, que son la misma con el
 * ejemplo descargado o sin él); la vía MANUAL la pone cada paso con su propio
 * formulario, porque los campos de una persona no son los de una cuenta
 * bancaria.
 *
 * Y entre elegir el archivo y escribir va SIEMPRE la previsualización, que no
 * es una cortesía: `POST /carga/{plantilla}/previsualizar` no escribe nada y
 * dice cuántas filas entrarían, cuántas ya existen y qué columnas no reconoció.
 * Cargar 300 clientes a ciegas y descubrir después que la columna del
 * identificador fiscal se ignoró entera obliga a limpiar a mano lo que entró
 * mal — y en un traspaso eso pasa en la primera carga, no en la décima.
 *
 * ---------------------------------------------------------------------------
 * «CONTINUAR CON ERRORES» SE OFRECE, Y SE DICE QUÉ SIGNIFICA
 *
 * Un CSV de verdad trae filas malas. Rechazar el archivo entero por tres filas
 * de 400 es lo que hace que alguien abandone; escribir las 397 en silencio es
 * peor. Se ofrece seguir con las buenas, diciendo cuántas se quedan fuera.
 */

export interface CargaEnSitioProps {
  /** El id de la plantilla de `GET /carga/plantillas`. */
  plantilla: string
  /** Qué se está cargando, en una línea y en la voz de Marty. */
  explicacion: React.ReactNode
  /** El motivo que queda en la pista de auditoría. */
  motivo: string
  /**
   * Qué invalidar además de los pasos del traspaso. El estado de cada paso se
   * DERIVA de los datos, así que escribir no basta: hay que volver a preguntar.
   */
  invalidar?: string[]
}

export function CargaEnSitio({
  plantilla, explicacion, motivo, invalidar = [],
}: CargaEnSitioProps) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const entrada = React.useRef<HTMLInputElement>(null)
  const [archivo, setArchivo] = React.useState<File | null>(null)
  const [previa, setPrevia] = React.useState<Previsualizacion | null>(null)
  const [conErrores, setConErrores] = React.useState(false)

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ["onboarding-pasos"] })
    for (const k of invalidar) void qc.invalidateQueries({ queryKey: [k] })
  }

  const limpiar = () => {
    setArchivo(null)
    setPrevia(null)
    setConErrores(false)
    if (entrada.current) entrada.current.value = ""
  }

  const mirar = useMutation({
    mutationFn: (f: File) => previsualizar(plantilla, f),
    onSuccess: (p) => setPrevia(p),
    onError: (e: unknown) => {
      // NO se guarda el archivo si no se pudo leer: dejarlo puesto con el botón
      // de cargar activo invita a escribir lo que nadie pudo mirar.
      limpiar()
      toast({
        variant: "warning",
        title: t("No pude leer ese archivo"),
        description: e instanceof Error ? e.message : String(e),
      })
    },
  })

  const escribir = useMutation({
    mutationFn: () => cargar(plantilla, archivo as File, motivo, conErrores),
    onSuccess: (r) => {
      const partes = [`${r.creadas} nueva(s)`, `${r.actualizadas} actualizada(s)`]
      if (r.errores?.length) partes.push(`${r.errores.length} con error`)
      toast({ title: t("Lo cargué"), description: partes.join(" · ") })
      limpiar()
      refrescar()
    },
    onError: (e: unknown) =>
      toast({
        variant: "destructive",
        title: t("No se pudo cargar"),
        description: e instanceof Error ? e.message : String(e),
      }),
  })

  const elegir = (f: File | null) => {
    if (!f) return
    setArchivo(f)
    setPrevia(null)
    setConErrores(false)
    mirar.mutate(f)
  }

  const errores = previa?.errores ?? []
  const puedeEscribir = !!previa && (errores.length === 0 || conErrores)

  return (
    <div className="space-y-2">
      <p className="text-2xs leading-5 text-muted-foreground">{explicacion}</p>

      <input
        ref={entrada}
        type="file"
        /*
          EXCEL PRIMERO, que es el formato de la plantilla que repartimos. El CSV
          se queda: quien exporta de otro sistema trabaja así, y el lector
          detecta cuál es por el CONTENIDO y no por la extensión.
        */
        accept=".xlsx,.csv,.tsv,.txt,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
        className="sr-only"
        onChange={(e) => elegir(e.target.files?.[0] ?? null)}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" disabled={mirar.isPending}
                onClick={() => entrada.current?.click()}>
          {mirar.isPending ? <Loader2 className="animate-spin" /> : <Upload />}
          {mirar.isPending ? t("Mirándolo…") : t("Elegir un archivo")}
        </Button>
        {/*
          LA PLANTILLA SE OFRECE DESPUÉS DEL ARCHIVO, no antes. Quien ya tiene su
          hoja no necesita descargar nada, y poner «descarga la plantilla»
          primero sugiere que hay que rellenarla para poder empezar — que es
          justo la fricción que el traspaso promete no tener.

          PERO TIENE QUE PARECER UN BOTÓN. Cesar, punto 2 de la cuarta tanda:
          «las plantillas de ejemplo deberían de ser más claras que se pueden
          descargar, hazlas como botón». Era `variant="ghost"` —sin fondo ni
          borde— al lado de uno que sí los tenía, así que se leía como texto de
          apoyo. Ir después no es lo mismo que no ser una acción.
        */}
        <Button size="sm" variant="outline"
                onClick={() => void descargarEjemplo(plantilla)}>
          <Download />
          {t("Descargar plantilla de Excel")}
        </Button>
      </div>

      {archivo ? (
        <p className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          <FileSpreadsheet className="h-3 w-3" aria-hidden="true" />
          <span className="truncate">{archivo.name}</span>
        </p>
      ) : null}

      {previa ? (
        <div
          className={cn(
            "space-y-1.5 rounded-md border px-2.5 py-2",
            errores.length ? "border-warning/40 bg-warning-soft" : "bg-surface",
          )}
        >
          <div className="flex flex-wrap items-center gap-1.5">
            {errores.length ? (
              <TriangleAlert className="h-3.5 w-3.5 text-warning" aria-hidden="true" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden="true" />
            )}
            <span className="text-xs font-medium">
              {t("Leí")} <span className="num-strong">{previa.filas_leidas}</span> {t("filas")}
            </span>
            <Badge variant="outline">{previa.nuevas} {t("nuevas")}</Badge>
            <Badge variant="outline">{previa.existentes} {t("ya las tenía")}</Badge>
            {errores.length ? (
              <Badge variant="warning-soft" estado>{errores.length} {t("con error")}</Badge>
            ) : null}
          </div>

          <p className="text-2xs leading-5 text-muted-foreground">
            {t("Todavía no he escrito nada.")} {previa.nota}
          </p>

          {/*
            LAS COLUMNAS IGNORADAS SE DICEN. Una columna que no se reconoce no
            rompe nada y se pierde entera: es la forma silenciosa de cargar un
            maestro a medias, y sólo se descubre semanas después cuando falta un
            dato que «sí estaba en el Excel».
          */}
          {previa.columnas_ignoradas.length ? (
            <p className="text-2xs leading-5">
              <strong className="font-medium">{t("No reconocí estas columnas y no las voy a cargar:")}</strong>{" "}
              <span className="code">{previa.columnas_ignoradas.join(", ")}</span>
            </p>
          ) : null}

          {errores.length ? (
            <>
              <ul className="space-y-0.5 text-2xs leading-5">
                {errores.slice(0, 4).map((e, i) => (
                  <li key={i}>
                    <span className="code">{t("fila")} {e.linea}</span> · {e.motivo}
                  </li>
                ))}
                {errores.length > 4 ? (
                  <li className="text-muted-foreground">
                    {t("y")} {errores.length - 4} {t("más")}
                  </li>
                ) : null}
              </ul>
              <label className="flex items-start gap-1.5 text-2xs leading-5">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={conErrores}
                  onChange={(e) => setConErrores(e.target.checked)}
                />
                <span>
                  {t("Cargar las")}{" "}
                  <strong className="font-medium">{previa.filas_leidas - errores.length}</strong>{" "}
                  {t("filas buenas y dejar fuera las que tienen error. Las que queden fuera no se cargan a medias: no entran.")}
                </span>
              </label>
            </>
          ) : null}

          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            <Button size="sm" disabled={!puedeEscribir || escribir.isPending}
                    onClick={() => escribir.mutate()}>
              {escribir.isPending ? <Loader2 className="animate-spin" /> : null}
              {escribir.isPending ? t("Cargando…") : t("Cargarlo")}
            </Button>
            <Button size="sm" variant="ghost" onClick={limpiar}>
              {t("Cancelar")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
